import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useCallback, useEffect, useRef } from 'react'
import { AppState, type AppStateStatus, Platform } from 'react-native'
import { useActiveEntry } from '@/hooks/useActiveEntry'
import { useOrgBySlug } from '@/hooks/useOrgBySlug'
import { useAuth } from '@/lib/auth'
import { getCurrentISOString } from '@/lib/dates'
import { type DeviceLocationData, getDeviceLocation } from '@/lib/device-location'
import { captureException } from '@/lib/errors'
import { buildCandidates } from '@/lib/geofence-candidates'
import { type GeofenceCandidate, pickContainingGeofence } from '@/lib/geofence-match'
import { log } from '@/lib/logger'
import { notify } from '@/lib/notice'
import { offlineExecutor } from '@/lib/offline'
import { newRecordId, useStore } from '@/lib/pocketbase'
import type { Locations } from '@/types/pbSchema'

const COOLDOWN_MS = 60_000
const NOTICE_DURATION_MS = 6_000

type TimeEntriesCollection = ReturnType<typeof useStore<['time_entries']>>[0]

interface InsertParams {
    entryId: string
    userId: string
    match: GeofenceCandidate
    fix: DeviceLocationData
    timeEntriesCollection: TimeEntriesCollection
}

function insertGeofenceEntry({
    entryId,
    userId,
    match,
    fix,
    timeEntriesCollection,
}: InsertParams): void {
    const tx = offlineExecutor.createOfflineTransaction({ mutationFnName: 'syncTimeEntries' })
    tx.mutate(() => {
        timeEntriesCollection.insert({
            id: entryId,
            user: userId,
            org: match.org,
            job: match.job,
            location: match.id,
            clock_in: getCurrentISOString(),
            clock_out: '',
            status: 'active',
            entry_type: 'geofence',
            notes: '',
            duration: 0,
            approved_by: '',
            approved_at: '',
            modified_by: '',
            rejection_reason: '',
            clock_in_location: {
                lat: fix.lat,
                lng: fix.lng,
                speed: fix.speed,
                heading: fix.heading,
                movement_type: fix.movement_type,
            },
            clock_out_location: null,
        })
    })
}

function undoGeofenceEntry(entryId: string, timeEntriesCollection: TimeEntriesCollection): void {
    try {
        const tx = offlineExecutor.createOfflineTransaction({ mutationFnName: 'syncTimeEntries' })
        tx.mutate(() => {
            timeEntriesCollection.delete(entryId)
        })
        log.info('Undid auto-started geofence entry', { entryId })
    } catch (error) {
        captureException('Undo auto-geofence failed', error)
    }
}

interface MatchAndCreateParams {
    candidates: GeofenceCandidate[]
    userId: string
    locationRows: Locations[]
    timeEntriesCollection: TimeEntriesCollection
    suppressed: Set<string>
    isStillEligible: () => boolean
}

/**
 * Performs the post-fix portion of the auto-start flow: get GPS fix, find a
 * matching geofence, re-check eligibility, insert the entry, and show the
 * notice. Returns true if an entry was created, false otherwise.
 */
async function matchAndCreateGeofenceEntry({
    candidates,
    userId,
    locationRows,
    timeEntriesCollection,
    suppressed,
    isStillEligible,
}: MatchAndCreateParams): Promise<boolean> {
    const fix = await getDeviceLocation()
    if (!fix) return false

    const match = pickContainingGeofence(
        { lat: fix.lat, lng: fix.lng, accuracy: fix.accuracy },
        candidates
    )
    if (!match) {
        // We had a usable fix and candidates but matched nothing — most often a
        // coarse fix (accuracy > geofence radius) rejected by the accuracy floor,
        // or the user is genuinely outside every site. Log so on-site auto-start
        // failures are diagnosable instead of silently doing nothing.
        log.debug('Auto-geofence: fix matched no geofence', {
            accuracy: fix.accuracy,
            candidateCount: candidates.length,
        })
        return false
    }

    // Re-check eligibility post-await: state may have changed (user clocked in
    // via another path, became inactive, etc.).
    if (!isStillEligible()) return false

    const entryId = newRecordId()
    insertGeofenceEntry({ entryId, userId, match, fix, timeEntriesCollection })
    log.info('Auto-started geofence entry on foreground', {
        entryId,
        location: match.id,
        job: match.job,
    })

    showAutoStartedNotice({
        entryId,
        matchedName: locationRows.find(loc => loc.id === match.id)?.name,
        locationId: match.id,
        timeEntriesCollection,
        suppressed,
    })
    return true
}

interface ShowAutoStartedNoticeParams {
    entryId: string
    matchedName: string | undefined
    locationId: string
    timeEntriesCollection: TimeEntriesCollection
    suppressed: Set<string>
}

function showAutoStartedNotice({
    entryId,
    matchedName,
    locationId,
    timeEntriesCollection,
    suppressed,
}: ShowAutoStartedNoticeParams): void {
    notify.success(
        matchedName ? `Started tracking at ${matchedName}` : 'Started tracking at this site',
        {
            duration: NOTICE_DURATION_MS,
            actions: [
                {
                    label: 'Undo',
                    onPress: () => {
                        undoGeofenceEntry(entryId, timeEntriesCollection)
                        suppressed.add(locationId)
                    },
                    dismissOnPress: true,
                },
            ],
        }
    )
}

/**
 * Auto-creates a geofence time entry when the user foregrounds the app while
 * physically inside a job-site geofence with no active timer/geofence/travel
 * entry. Compensates for OS geofence ENTER events that fire late or not at all.
 *
 * Mounted on the entry screen — the only place a missing entry is visible to
 * the user.
 */
export function useAutoGeofenceOnForeground(orgSlug: string): void {
    const { user } = useAuth()
    const { org, trackingPausedUntil } = useOrgBySlug(orgSlug)
    const { activeEntry } = useActiveEntry()
    const [locationsCollection, timeEntriesCollection] = useStore('locations', 'time_entries')

    const lastCheckAtRef = useRef<number>(0)
    const suppressedLocationIdsRef = useRef<Set<string>>(new Set())
    const checkInFlightRef = useRef<boolean>(false)

    // Same locations the OS-level geofence registry was built from.
    const { data: locationRows = [] } = useLiveQuery(
        q =>
            org?.id
                ? q.from({ loc: locationsCollection }).where(({ loc }) => eq(loc.org, org.id))
                : undefined,
        [org?.id]
    )

    const isEligible = useCallback((): boolean => {
        if (Platform.OS === 'web') return false
        if (checkInFlightRef.current) return false
        if (!user?.id || !org?.id) return false
        if (user.geo_tracking_disabled) return false
        if (trackingPausedUntil && new Date(trackingPausedUntil).getTime() > Date.now()) {
            return false
        }
        if (activeEntry) return false
        return true
    }, [user, org, trackingPausedUntil, activeEntry])

    const tryAutoStart = useCallback(async () => {
        const now = Date.now()
        if (now - lastCheckAtRef.current < COOLDOWN_MS) return

        if (!isEligible() || !user?.id) return

        const candidates = buildCandidates(locationRows, suppressedLocationIdsRef.current)
        if (candidates.length === 0) return

        // Stamp the cooldown only once we're actually about to do the expensive
        // GPS fetch + match. Early bails above (data not loaded yet, ineligible,
        // no candidates) must NOT consume the window — otherwise a cold-start
        // attempt that bails before data loads would block the real check for a
        // full minute, defeating the purpose of this safety net.
        lastCheckAtRef.current = now

        checkInFlightRef.current = true
        try {
            await matchAndCreateGeofenceEntry({
                candidates,
                userId: user.id,
                locationRows,
                timeEntriesCollection,
                suppressed: suppressedLocationIdsRef.current,
                isStillEligible: isEligible,
            })
        } catch (error) {
            log.warn('Auto-geofence check failed', { error })
        } finally {
            checkInFlightRef.current = false
        }
    }, [isEligible, locationRows, timeEntriesCollection, user])

    // Initial mount: catches "user just navigated to the entry screen while
    // already foregrounded" — there's no AppState change to listen for.
    useEffect(() => {
        tryAutoStart()
    }, [tryAutoStart])

    // Foreground transitions; clear suppressions on background so the next
    // session re-evaluates with a clean slate.
    useEffect(() => {
        const subscription = AppState.addEventListener('change', (next: AppStateStatus) => {
            if (next === 'active') {
                tryAutoStart()
            } else if (next === 'background' || next === 'inactive') {
                suppressedLocationIdsRef.current.clear()
            }
        })
        return () => subscription.remove()
    }, [tryAutoStart])
}
